import { Box, Typography } from "@mui/material"
import type { FC, RefObject } from "react"
import { useMemo, useRef } from "react"
import { useFormContext } from "react-hook-form"

import type { WarehouseLocationFormValues } from "../../schemas"

import { AerosTextField, FormSelect, FormTextField } from "@/components/UiKit"
import { MENU_PROPS } from "@/constants/forms"
import type { Company, Location, WarehouseArea, WarehouseAreaOutputList } from "@/graphql/codegen/graphql"
import useDuplicateByFieldValidation from "@/hooks/useDuplicateByFieldValidation"
import type { UseGetBaseCompaniesReturn } from "@/hooks/useGetBaseCompanies"
import type { UseGetBaseLocationsReturn } from "@/hooks/useGetBaseLocations"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { FieldErrorType } from "@/types/common"
import type { Option } from "@/types/form-fields"
import { formatLocationAddress } from "@/utils/utils"

interface WarehouseLocationFormProps {
  warehouse: WarehouseArea | null
  warehouses: WarehouseAreaOutputList[]
  companiesMethods: UseGetBaseCompaniesReturn
  locationsMethods: UseGetBaseLocationsReturn
  onCompanySelect: (company: Company) => void
  onLocationSelect: (location: Location) => void
}

const WarehouseLocationForm: FC<WarehouseLocationFormProps> = ({
  warehouse,
  warehouses,
  companiesMethods,
  locationsMethods,
  onCompanySelect,
  onLocationSelect,
}) => {
  const {
    control,
    formState: { errors },
    setError,
    setValue,
    trigger,
    watch,
  } = useFormContext<WarehouseLocationFormValues>()
  const companyId = watch("companyId")
  const externalSystemId = watch("externalSystemId")
  const companiesSelectRef = useRef<HTMLDivElement>(null)
  const locationsSelectRef = useRef<HTMLDivElement>(null)
  const { companies, isFetchingCompanies, loadMoreCompanies } = companiesMethods
  const { locations, isFetchingLocations, loadMoreLocations } = locationsMethods

  useDuplicateByFieldValidation<WarehouseAreaOutputList>({
    query: externalSystemId,
    list: warehouses,
    fieldName: "externalSystemId",
    fieldLabel: "AS400 Warehouse ID",
    entityName: "Warehouse",
  })

  const companyOptions: Option<string>[] = useMemo(
    () =>
      companies.map((company) => ({
        value: company.companyId,
        label: company.name ?? "",
      })),
    [companies]
  )

  const locationOptions: Option<string>[] = useMemo(
    () =>
      locations.map((location) => ({
        value: location.locationId,
        label: formatLocationAddress(location),
      })),
    [locations]
  )

  const getSelectMenuProps = (ref: RefObject<unknown>) => ({
    ...MENU_PROPS,
    PaperProps: {
      ...MENU_PROPS.PaperProps,
      ref,
    },
  })

  const handleCompaniesScroll = useScrollLoadMore({
    fetching: isFetchingCompanies,
    loadMore: loadMoreCompanies,
    scrollContainerRef: companiesSelectRef,
  })

  const handleLocationsScroll = useScrollLoadMore({
    fetching: isFetchingLocations,
    loadMore: loadMoreLocations,
    scrollContainerRef: locationsSelectRef,
  })

  const handleCompanySelect = (companyId: string | null) => {
    setValue("locationId", "")
    if (companyId) {
      const company = companies.find((c) => c.companyId === companyId)
      if (company) {
        onCompanySelect(company)
      }
    }
  }
  const handleLocationSelect = (locationId: string | null) => {
    const location = locations.find((l) => l.locationId === locationId)
    if (location) {
      onLocationSelect(location)
    }
  }

  // Workaround to prevent async validation error from being overwritten due to form mode 'all' validation concurrency.
  const handleExternalSystemIdBlur = async () => {
    const error = errors.externalSystemId
    const duplicateError = error?.type === FieldErrorType.Duplicate ? error : null
    await trigger("externalSystemId")

    if (duplicateError) {
      setError("externalSystemId", {
        type: duplicateError.type,
        message: duplicateError.message,
      })
    }
  }

  return (
    <Box data-testid='warehouse-location-form'>
      <Typography variant='body1' fontWeight={700} className='mb-6 leading-6 tracking-[0.15px] text-gray-700'>
        1. Address Details
      </Typography>
      <Box className='flex flex-col gap-6'>
        {warehouse ? (
          <>
            <AerosTextField
              name='companyId'
              id='warehouse-company'
              label='Company'
              required
              data-testid='warehouse-company-input'
              value={warehouse.location?.company?.name}
              disabled
            />
            <AerosTextField
              id='warehouse-location'
              label='Address'
              required
              data-testid='warehouse-location-input'
              value={formatLocationAddress(warehouse.location)}
              disabled
            />
          </>
        ) : (
          <>
            <FormSelect
              control={control}
              name='companyId'
              label='Company'
              required
              placeholder='Please Select'
              options={companyOptions}
              data-testid='warehouse-company-select'
              onChange={handleCompanySelect}
              onScrollCapture={handleCompaniesScroll}
              MenuProps={getSelectMenuProps(companiesSelectRef)}
            />
            <FormSelect
              control={control}
              name='locationId'
              label='Address'
              required
              placeholder='Please Select'
              options={locationOptions}
              data-testid='warehouse-location-select'
              disabled={!companyId}
              onChange={handleLocationSelect}
              onScrollCapture={handleLocationsScroll}
              MenuProps={getSelectMenuProps(locationsSelectRef)}
            />
          </>
        )}
        <FormTextField
          control={control}
          name='externalSystemId'
          id='warehouse-as400-id-input'
          label='AS400 Warehouse ID'
          required
          data-testid='warehouse-as400-id-input'
          onBlur={handleExternalSystemIdBlur}
        />
      </Box>
    </Box>
  )
}

export default WarehouseLocationForm
